import { formatNumber } from "@angular/common";
import { Component, EventEmitter, Inject, Input, LOCALE_ID, OnChanges, Output, SimpleChanges } from "@angular/core";
import { abbreviate } from "../../charts/bar-chart/bar-chart.component";
import { FILTER_MODEL_KEY, Linqset, ORGANISATION_MODEL_KEY } from "../../common/linqset-model";
import { DEFAULT_CURRENCY, getCurrency } from "../../common/organisation";
import { colorAsARGB } from "../../model-services/model-item-enums";
import { OrganisationService } from "../../organisation/organisation.service";
import { InsightsFieldType } from "../model/insights-field-type";
import { NodeTypeData } from "../node-types/node-type-data";
import { heatmapData, HeatmapRow } from "./node-chart-data";

// roles, people, systems or infrastructure against the models they work in, each cell shaded by how much: one hue, light
// to dark, so where the work sits and how spread out each node is can be seen at a glance
@Component({
    selector: 'app-node-heatmap-chart',
    templateUrl: './node-heatmap.html',
    styleUrls: ['./node-charts.css'],
    standalone: false
})
export class NodeHeatmapComponent implements OnChanges {
    @Input() linqset: Linqset
    @Input() typeData: NodeTypeData
    @Input() fieldType: InsightsFieldType
    @Input() trigger: any
    @Input() topNumber: number

    @Output() selectNodeEvent = new EventEmitter<string>();

    rows: HeatmapRow[] = []
    shown: HeatmapRow[] = []
    columns: { key: string, name: string, total: number }[] = []
    max = 0
    private rgb = { r: 91, g: 97, b: 114 }

    constructor(@Inject(LOCALE_ID) private locale: string, private organisationService: OrganisationService) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['trigger'] || changes['typeData'] || changes['fieldType'] || changes['topNumber'] || changes['linqset']) this.populate();
    }

    get fieldName() {
        return String(this.fieldType?.type || '').toLowerCase();
    }

    get spread() {
        return this.rows.filter(r => r.cells.filter(c => c > 0).length > 1).length;
    }

    private populate() {
        if (!this.typeData || !this.linqset || !this.fieldType?.isValid()) return;
        const workspaceKey = this.linqset.workspaceKey;
        const models = (this.linqset.organisation?.models || []).map(m => ({ key: m._key, name: m.name }));
        const items = (this.typeData.list || []).filter(n => !n.excluded).map((n, i) => ({
            key: n._key, name: n.name || 'Missing name ' + i,
            byModel: (n.insights || []).filter(ins => ins.projectKey === workspaceKey && ins.modelKey !== FILTER_MODEL_KEY && ins.modelKey !== ORGANISATION_MODEL_KEY)
                .reduce((m, ins) => ({ ...m, [ins.modelKey]: this.fieldType.getSingleValueFromInsights(ins, this.typeData.dataType) || 0 }), {} as Record<string, number>),
        }));
        const result = heatmapData(items, models);
        this.rows = result.rows;
        this.columns = result.columns;
        this.max = result.max;
        this.shown = this.rows.slice(0, this.topNumber || 35);
        const colour = this.typeData.colour || '#5b6172';
        this.rgb = colorAsARGB(colour) || this.rgb;
    }

    // how strong a cell is, 0 to 1: on a log scale, as one large node would otherwise leave every other cell pale
    private strength(v: number) {
        return v > 0 && this.max > 0 ? 0.12 + 0.88 * Math.log1p(v) / Math.log1p(this.max) : 0;
    }

    // a cell's shade: none when there's nothing, then light to the type's full colour at the largest
    shade(v: number) {
        const a = this.strength(v);
        return a ? `rgba(${this.rgb.r},${this.rgb.g},${this.rgb.b},${a.toFixed(3)})` : 'transparent';
    }

    // the legend's steps, from least to most, spaced as the shading is
    get steps() {
        return [0.05, 0.25, 0.5, 0.75, 1].map(s => Math.expm1(s * Math.log1p(this.max)));
    }

    // dark cells take light text
    dark(v: number) {
        return this.strength(v) > 0.62;
    }

    value(v: number, short = true) {
        if (!v) return '';
        const symbol = this.fieldType?.isCurrency() ? getCurrency(this.organisationService.organisation?.currencyCode || DEFAULT_CURRENCY).symbol : '';
        const units = !symbol && this.fieldType?.getUnits() ? ' ' + this.fieldType.getUnits() : '';
        return symbol + (short ? abbreviate(v, this.locale) : formatNumber(Math.round(v), this.locale, '1.0-0') + units);
    }

    tip(row: HeatmapRow, i: number) {
        const v = row.cells[i];
        return `${row.name} in ${this.columns[i].name}: ${v ? this.value(v, false) : 'none'}${v && row.total ? ` (${Math.round(100 * v / row.total)}% of its ${this.fieldName})` : ''}`;
    }

    select(row: HeatmapRow) {
        this.selectNodeEvent.emit(row.key);
    }
}
